export type DenialReason = 'plan_restriction' | 'country_restriction' | 'user_permission' | 'runtime_restriction';

export interface MenuVisibilityEntry {
    accessible: boolean;
    reason: DenialReason | null;
    target: string | null;
    target_label: string | null;
    cross_family: boolean | null;
}

export type MenuVisibilityMap = Record<string, MenuVisibilityEntry>;

export const useMenuVisibilityStore = defineStore('MenuVisibilityStore', () => {
    const visibility = ref<MenuVisibilityMap>({});
    const loaded = ref(false);
    const loading = ref(false);

    const load = async () => {
        const authStore = useAuthStore();
        const config = useRuntimeConfig();
        const baseUrl = (config.public.worksiteApiUrl as string) || '';

        if (!authStore.usableToken || !authStore.tenant) return;

        loading.value = true;
        try {
            const response = await globalThis.fetch(`${baseUrl}/api/v1/tenant/common/menu-visibility`, {
                headers: {
                    Authorization: authStore.usableToken,
                    'X-Tenant-Id': authStore.tenant.getId(),
                    Accept: 'application/json',
                },
            });
            if (response.ok) {
                const json = await response.json();
                const flat: MenuVisibilityMap = {};
                for (const [key, value] of Object.entries(json.data as Record<string, unknown>)) {
                    const entry = value as Record<string, unknown>;
                    if ('accessible' in entry) {
                        flat[key] = entry as unknown as MenuVisibilityEntry;
                    } else {
                        for (const [permKey, permEntry] of Object.entries(entry)) {
                            flat[permKey] = permEntry as MenuVisibilityEntry;
                        }
                    }
                }
                visibility.value = flat;
                loaded.value = true;
            }
        } catch (e) {
            console.error('Failed to fetch menu visibility', e);
        } finally {
            loading.value = false;
        }
    };

    const getEntry = (permissionKey: string): MenuVisibilityEntry | undefined => {
        return visibility.value[permissionKey];
    };

    const isAccessible = (permissionKey: string): boolean => {
        const entry = getEntry(permissionKey);
        if (!entry) return true;
        return entry.accessible;
    };

    const getDenialReason = (permissionKey: string): DenialReason | null => {
        const entry = getEntry(permissionKey);
        if (!entry) return null;
        return entry.reason;
    };

    const getTarget = (permissionKey: string): string | null => {
        const entry = getEntry(permissionKey);
        if (!entry) return null;
        return entry.target;
    };

    const getTargetLabel = (permissionKey: string): string | null => {
        const entry = getEntry(permissionKey);
        if (!entry) return null;
        return entry.target_label;
    };

    const isCrossFamily = (permissionKey: string): boolean => {
        const entry = getEntry(permissionKey);
        return entry?.cross_family ?? false;
    };

    useListen('tenancy:tenant:updated', () => {
        load();
    });

    // Load (and reload) the visibility map whenever a tenant becomes available. The login
    // flow sets the tenant then client-side-navigates without calling load() itself, so
    // relying only on imperative calls left the badges missing until a manual refresh.
    // Watching the tenant id covers login, refresh and tenant switch uniformly.
    const authStore = useAuthStore();
    watch(
        () => authStore.tenant?.getId(),
        (tenantId) => {
            if (tenantId) load();
        },
        { immediate: true }
    );

    const $reset = () => {
        visibility.value = {};
        loaded.value = false;
        loading.value = false;
    };

    return {
        visibility,
        loaded,
        loading,
        load,
        getEntry,
        isAccessible,
        getDenialReason,
        getTarget,
        getTargetLabel,
        isCrossFamily,
        $reset,
    };
});
